import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import Shell from "@/components/Shell";
import LandingPage from "@/pages/Landing";
import SessionsPage from "@/pages/Sessions";
import SessionSetup from "@/pages/SessionSetup";
import CameraPage from "@/pages/Camera";
import GalleryPage from "@/pages/Gallery";
import SyncPage from "@/pages/Sync";
import EventsPage from "@/pages/Events";
import WebhooksPage from "@/pages/Webhooks";
import AdminPage from "@/pages/Admin";
import ParentRedirect from "@/pages/ParentRedirect";
import LoginPage from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import CameraHelpPage from "@/pages/CameraHelp";
import SuiteHome from "@/pages/suite/Home";
import SuiteDashboard from "@/pages/suite/Dashboard";
import SuiteBatch from "@/pages/suite/Batch";
import SuiteBilling from "@/pages/suite/Billing";
import SuiteBillingReturn from "@/pages/suite/BillingReturn";
import SuiteAccount from "@/pages/suite/Account";
import ContactPage from "@/pages/Contact";
import ComparePage from "@/pages/Compare";
import UseCasePage from "@/pages/UseCase";
import DroneWorkflowPage from "@/pages/DroneWorkflow";
import EnterprisePage from "@/pages/Enterprise";
import WatcherPage from "@/pages/Watcher";
import LocatePage from "@/pages/Locate";
import SurveyPage from "@/pages/Survey";
import { RequireAuth } from "@/components/RequireAuth";
import { AuthProvider } from "@/context/AuthContext";
import { maybeRedirectToProduction } from "@/lib/prodRedirect";

// Production domain cutover — INERT until REACT_APP_REDIRECT_TO_PROD=true
// in frontend/.env. Flip the flag the moment DNS for aiimagesuites.pro is live.
maybeRedirectToProduction();

// Routes that live INSIDE Shell (with header + bottom nav)
function ShellRoutes() {
  const guard = (el) => <RequireAuth>{el}</RequireAuth>;
  return (
    <Shell>
      <Routes>
        <Route path="/sessions" element={guard(<SessionsPage />)} />
        <Route path="/setup" element={guard(<SessionSetup />)} />
        <Route path="/camera" element={guard(<CameraPage />)} />
        <Route path="/gallery" element={guard(<GalleryPage />)} />
        <Route path="/sync" element={guard(<SyncPage />)} />
        <Route path="/survey" element={guard(<SurveyPage />)} />
        <Route path="/watcher" element={guard(<WatcherPage />)} />
        <Route path="/locate" element={guard(<LocatePage />)} />
        <Route path="/events" element={guard(<EventsPage />)} />
        <Route path="/webhooks" element={guard(<WebhooksPage />)} />
        {/* Admin has its own token gate; camera help is public documentation. */}
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/help/camera" element={<CameraHelpPage />} />
        {/* Unknown URLs go to the public landing — never to an app surface. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}

// AppRouter — checks for `session_id=` in the URL fragment SYNCHRONOUSLY
// during render (before ANY /api/auth/me runs). This prevents the race where
// AuthContext.refresh() 401s and clobbers the callback in flight.
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Landing is the marketing surface — rendered OUTSIDE Shell so it
          has no app chrome. Returning users typically deep-link to /sessions
          via bookmarks or the PWA shortcut. */}
      <Route path="/" element={<SuiteHome />} />
      <Route path="/dashboard" element={<RequireAuth><SuiteDashboard /></RequireAuth>} />
      <Route path="/batch" element={<RequireAuth><SuiteBatch /></RequireAuth>} />
      <Route path="/billing" element={<RequireAuth><SuiteBilling /></RequireAuth>} />
      <Route path="/billing/return" element={<RequireAuth><SuiteBillingReturn /></RequireAuth>} />
      <Route path="/account" element={<RequireAuth><SuiteAccount /></RequireAuth>} />
      <Route path="/capture" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      {/* Public marketing pages — must never fall through to the sessions list. */}
      <Route path="/pricing" element={<Navigate to="/#pricing" replace />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/compare/capture-time-naming-vs-renaming" element={<ComparePage />} />
      <Route path="/compare" element={<Navigate to="/compare/capture-time-naming-vs-renaming" replace />} />
      <Route path="/use-cases/:slug" element={<UseCasePage />} />
      <Route path="/use-cases" element={<Navigate to="/use-cases/studio-batch-rename-exif" replace />} />
      <Route path="/workflows/drone" element={<DroneWorkflowPage />} />
      <Route path="/workflows" element={<Navigate to="/workflows/drone" replace />} />
      <Route path="/enterprise" element={<EnterprisePage />} />
      {/* Signup = Google sign-in, then land on Cloud Sync (Stripe checkout). */}
      <Route path="/signup" element={<Navigate to="/login?intent=sync&next=%2Fsync" replace />} />
      <Route path="/register" element={<Navigate to="/login?intent=sync&next=%2Fsync" replace />} />
      <Route path="/signin" element={<Navigate to="/login" replace />} />
      {/* iter-31 — parent-product typo catcher. */}
      <Route path="/editor" element={<ParentRedirect />} />
      <Route path="/image-editor" element={<ParentRedirect />} />
      <Route path="/gallery-editor" element={<ParentRedirect />} />
      <Route path="/ai-suite" element={<ParentRedirect />} />
      <Route path="/suite" element={<ParentRedirect />} />
      {/* Everything else gets the app Shell. */}
      <Route path="/*" element={<ShellRoutes />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <ThemedToaster />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

const SUITE_PATHS = /^\/($|dashboard|batch|billing|account)/;
const NEON_TOAST = {
  border: "1px solid rgba(0,242,254,0.35)", borderRadius: "14px", background: "rgba(11,15,23,0.95)", color: "#E2E8F0",
  fontFamily: "IBM Plex Sans, sans-serif", fontSize: "13px", boxShadow: "0 0 24px rgba(147,51,234,0.25)",
};

function ThemedToaster() {
  const { pathname } = useLocation();
  const suite = SUITE_PATHS.test(pathname);
  return <Toaster position={suite ? "bottom-right" : "top-center"} toastOptions={{ style: NEON_TOAST, className: "suite-toast" }} />;
}

export default App;
